import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import Times from '~/bootcamp/Activities/components/Times'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { addParenthesis, generateTerms } from './helper'

export default function ManyNegative3({
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  difficultyLevel,
  onNextQuestionClick,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const [answer, setAnswer] = useState<string>('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation('generics')
  useFocus(autofocus, answerState, inputRef)

  const terms = useMemo(() => {
    setAnswer('')
    return generateTerms('MANY_NEGATIVE_3', difficultyLevel, taskHistory)
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  useEmitInstance(onInstanceChange, terms.length ? `${addParenthesis(terms).join(' \\cdot ')} = \\square` : null)

  const handleOnCheckAnswerClick = (): void => {
    const accumulated = terms.reduce((prev, curr) => prev * curr, 1)
    const isCorrect = parseInt(answer, 10) === accumulated
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory(terms.slice(0, 3))
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <MultiplicationActivityWrapper>
        <span>
          {addParenthesis(terms).map((term, index) => {
            return (
              <>
                <span>{term}</span> {terms.length - 1 !== index ? <Times /> : ''}
              </>
            )
          })}
        </span>
        <span className="block sm:inline-block">
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertProduct')}
            autoFocus={autofocus}
            isError={answerState === false}
            isWide
            key={inputKey}
            maxDigits={15}
            onChange={(value) => setAnswer(value)}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </span>
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}
